import { Injectable } from '@angular/core';
import { Actions, ofAction, ofActionCompleted, ofActionErrored } from '@ngxs/store';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { 
  LoadBankAccounts, 
  LoadBankAccountsSuccess, 
  LoadBankAccountsFailure,
  CreateBankAccount,
  CreateBankAccountSuccess,
  CreateBankAccountFailure,
  UpdateBankAccount,
  UpdateBankAccountSuccess,
  UpdateBankAccountFailure,
  LoadCreditCards,
  LoadCreditCardsSuccess,
  LoadCreditCardsFailure,
  CreateCreditCard,
  CreateCreditCardSuccess,
  CreateCreditCardFailure,
  UpdateCreditCard,
  UpdateCreditCardSuccess,
  UpdateCreditCardFailure
} from '../states/payment-methods.state';
import { BankAccountService, CreditCardService, BankAccount } from '@ops/shared-types';

@Injectable()
export class PaymentMethodsEffects {
  constructor(
    private actions$: Actions,
    private bankAccountService: BankAccountService,
    private creditCardService: CreditCardService
  ) {
    // Initialize effects after constructor
    this.initializeEffects();
  }

  private initializeEffects(): void {
    // Bank Account Effects
    this.actions$.pipe(
      ofAction(LoadBankAccounts),
      switchMap((action: LoadBankAccounts) =>
        this.bankAccountService.getAllBankAccountsForCompany(action.companyId).pipe(
          map((bankAccounts) => new LoadBankAccountsSuccess(bankAccounts)),
          catchError((error) => of(new LoadBankAccountsFailure(error.message || 'Failed to load bank accounts')))
        )
      )
    ).subscribe();

    this.actions$.pipe(
      ofAction(CreateBankAccount),
      switchMap((action: CreateBankAccount) => {
        // Ensure required fields are present
        const bankAccountData = {
          ...action.bankAccount,
          description: action.bankAccount.description || '',
          shortName: action.bankAccount.shortName || '',
          currencyCode: action.bankAccount.currencyCode || 'USD'
        } as BankAccount;
        
        return this.bankAccountService.createBankAccount(bankAccountData, action.companyId).pipe(
          map((bankAccount) => new CreateBankAccountSuccess(bankAccount)),
          catchError((error) => of(new CreateBankAccountFailure(error.message || 'Failed to create bank account')))
        );
      })
    ).subscribe();

    this.actions$.pipe(
      ofAction(UpdateBankAccount),
      switchMap((action: UpdateBankAccount) =>
        this.bankAccountService.updateBankAccount(action.bankAccount, action.bankAccount._id, action.companyId).pipe(
          map((bankAccount) => new UpdateBankAccountSuccess(bankAccount)),
          catchError((error) => of(new UpdateBankAccountFailure(error.message || 'Failed to update bank account')))
        )
      )
    ).subscribe();

    // Credit Card Effects
    this.actions$.pipe(
      ofAction(LoadCreditCards),
      switchMap((action: LoadCreditCards) =>
        this.creditCardService.getCreditCardsForCompany(action.companyId).pipe(
          map((creditCards) => new LoadCreditCardsSuccess(creditCards)),
          catchError((error) => of(new LoadCreditCardsFailure(error.message || 'Failed to load credit cards')))
        )
      )
    ).subscribe();

    this.actions$.pipe(
      ofAction(CreateCreditCard),
      switchMap((action: CreateCreditCard) =>
        this.creditCardService.createCreditCard(action.creditCard, action.companyId).pipe(
          map((creditCard) => new CreateCreditCardSuccess(creditCard)),
          catchError((error) => of(new CreateCreditCardFailure(error.message || 'Failed to create credit card')))
        )
      )
    ).subscribe();

    this.actions$.pipe(
      ofAction(UpdateCreditCard),
      switchMap((action: UpdateCreditCard) =>
        this.creditCardService.updateCreditCard(action.creditCard, action.creditCard._id, action.companyId).pipe(
          map((creditCard) => new UpdateCreditCardSuccess(creditCard)),
          catchError((error) => of(new UpdateCreditCardFailure(error.message || 'Failed to update credit card')))
        )
      )
    ).subscribe();

    // Logging effects for debugging
    this.actions$.pipe(
      ofActionCompleted(LoadBankAccounts, CreateBankAccount, UpdateBankAccount),
      tap((action) => {
        console.log('Bank Account Action Completed:', action.action.constructor.name);
      })
    ).subscribe();

    this.actions$.pipe(
      ofActionCompleted(LoadCreditCards, CreateCreditCard, UpdateCreditCard),
      tap((action) => {
        console.log('Credit Card Action Completed:', action.action.constructor.name);
      })
    ).subscribe();

    // Error logging
    this.actions$.pipe(
      ofActionErrored(LoadBankAccounts, CreateBankAccount, UpdateBankAccount),
      tap((action) => {
        console.error('Bank Account Action Error:', action.action.constructor.name, action.result);
      })
    ).subscribe();

    this.actions$.pipe(
      ofActionErrored(LoadCreditCards, CreateCreditCard, UpdateCreditCard),
      tap((action) => {
        console.error('Credit Card Action Error:', action.action.constructor.name, action.result);
      })
    ).subscribe();
  }
}